import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * While a guarded page (product create/edit) is open in one tab, any other BO tab that loads
 * is told so over a BroadcastChannel. The new tab closes itself (works only when the browser
 * allows it) or shows a blocking notice; the original tab is focused and its title flashes.
 * Browsers don't let a page switch the user to another tab, so focus is best-effort.
 */
@Injectable({ providedIn: 'root' })
export class SingleTabGuardService {
    // True in a tab that was redirected away because a guarded page is open elsewhere.
    blocked = false;

    private readonly guardedPaths = ['/app/new-product'];
    private channel: BroadcastChannel | null = null;
    private holding = false;

    constructor(private router: Router, private zone: NgZone) { }

    init(): void {
        if (this.channel || typeof BroadcastChannel === 'undefined') return;

        this.channel = new BroadcastChannel('bo-single-tab-guard');
        this.channel.onmessage = (e) => this.zone.run(() => this.onMessage(e.data));

        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(e => this.holding = this.guardedPaths.some(p => e.urlAfterRedirects.startsWith(p)));

        this.channel.postMessage({ type: 'probe' });
    }

    closeThisTab(): void {
        window.close();
    }

    private onMessage(msg: { type: string }): void {
        if (msg?.type === 'probe' && this.holding) {
            this.channel.postMessage({ type: 'busy' });
            this.flashTitle();
            window.focus();
        } else if (msg?.type === 'busy' && !this.blocked) {
            this.blocked = true;
            window.close();
        }
    }

    private flashTitle(): void {
        const original = document.title;
        document.title = '⚠ Opened in another tab - ' + original;
        setTimeout(() => document.title = original, 4000);
    }
}
